/**
 * Platform-level API client.
 *
 * This module exports only environment-agnostic platform API calls
 * (gateway-level: /api/me, /api/environments, /api/jobs). All
 * environment-scoped operations live in
 * ``apps/web/src/api/environment-api.ts`` and require the caller to
 * pass the active environment name explicitly.
 *
 * ADR-0069 F199 (Wave 5): the previous version of this file
 * exposed dead duplicates of the supply-chain / scenario / training /
 * simulation / combined API functions with their paths hardcoded to
 * the example environment's gateway-proxy prefix. That hardcoding
 * routed supply-chain-visibility traffic through the example
 * environment's gateway proxy — meaning the analyst needed
 * modules/example verb grants to use SCV instead of the correct
 * modules/supply-chain-visibility verb grants. Niel R2 review
 * flagged this as an authorization side-effect bug. The fix: delete
 * the dead exports here. The active call sites
 * (SupplyChainTab.tsx, ScenariosTab.tsx, OptimizationTab.tsx, etc.)
 * already use environment-api.ts with explicit
 * moduleName: string parameters resolved from
 * useActiveModule() at the component level.
 *
 * Generated openapi-fetch clients in ``./platform-client.ts`` and
 * ``./example-client.ts`` remain available for components that
 * legitimately target the platform gateway or the (definitionally
 * example-only) example environment.
 */

import { platformClient } from './index'
import type { UserContext, ModuleSummary, Job, PaginatedResponse } from './types'

// Re-export ApiError from the generated client module
export { ApiError } from './platform-client'

// ── Internal: unwrap openapi-fetch response or throw ──────────────────

async function unwrapPlatform<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise
  if (!response.ok || error) {
    const { ApiError, formatApiError } = await import('./platform-client')
    const msg = formatApiError(error, `${response.status} ${response.statusText}`)
    throw new ApiError(response.status, msg)
  }
  return data as T
}

// ── Platform API (gateway, environment-agnostic) ──────────────────────

/** Get current user context. */
export async function getCurrentUser(): Promise<UserContext> {
  return unwrapPlatform<UserContext>(platformClient.GET('/api/me'))
}

/** List available modules. */
export async function listModules(): Promise<ModuleSummary[]> {
  return unwrapPlatform<ModuleSummary[]>(platformClient.GET('/api/modules'))
}

// ── Jobs API ──────────────────────────────────────────────────────────

/** List jobs with optional filters. */
export async function listJobs(
  params: { module?: string; status?: string; limit?: number; offset?: number } = {},
): Promise<PaginatedResponse<Job>> {
  return unwrapPlatform<PaginatedResponse<Job>>(
    platformClient.GET('/api/jobs', {
      params: { query: params as Record<string, unknown> },
    }),
  )
}

/** Get a specific job. */
export async function getJob(jobId: string): Promise<Job> {
  return unwrapPlatform<Job>(
    platformClient.GET('/api/jobs/{jobId}', { params: { path: { jobId } } }),
  )
}
